/**
 * The bedrock range slider — one definition for every `input[type="range"]` the
 * marketing site renders.
 *
 * It exists for one property that is easy to lose and invisible on a desktop:
 * a native range input is about **16 px tall**, and it is dragged with a
 * finger. Measured at 390 px with touch emulation, both calculator sliders were
 * 292×16 — a control whose whole purpose is a drag, at a third of the height
 * `ui-design.md` § 6 asks of a standalone control.
 *
 * The height is applied only under `pointer: coarse`, which is the honest
 * scope: it is a TOUCH requirement, and inflating the control for a mouse would
 * change a design that is correct for a mouse. The visual track is unaffected —
 * a native range centres its track in whatever box it is given, so this buys
 * hit area and changes nothing anyone sees on a desktop.
 *
 * `touch-action: none` is deliberate and is the second half of the fix. Without
 * it a vertical drag that starts on the slider scrolls the page instead of
 * moving the value, so on a tall page the control is reachable but not reliably
 * usable — the failure that reads as "the slider is broken on my phone".
 */

export interface RangeStyleProps {
  readonly className?: string | undefined;
}

export function rangeClasses({ className }: RangeStyleProps = {}): string {
  return [
    'block w-full accent-primary',
    // A 44px hit area on touch, the house floor for a standalone control.
    '[@media(pointer:coarse)]:min-h-11',
    '[@media(pointer:coarse)]:[touch-action:none]',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--bd-blue)]',
    'focus-visible:ring-offset-2',
    className,
  ]
    .filter(Boolean)
    .join(' ');
}
